import { flightTrackFingerprint, hydrateFlightTracks, latestFlightTrack } from "./aar";
import { deskChromeFromUnknown, type DeskChrome } from "./themePacks";
import { widgetLayoutScheduleBits } from "./widgetBoard";

/** Cheap Nanode revision + live-slice types. Schedule vs kinematics stay separate. */

export type SyncRevisionMeta = {
  scheduleRevision: string;
  liveRevision: string;
  exportedAtUtc: string;
  companyId: string;
  companyName: string;
  theme: DeskChrome["theme"];
  themePack: string;
  themeAccent: string;
  deskVersion: string;
};

export type SyncLiveSlice = {
  liveRevision: string;
  exportedAtUtc: string;
  crewPresence: unknown;
  lastKnownPosition: unknown;
  /** Desk feed that produced lastKnownPosition. Absent on older servers. */
  simSource: "simconnect" | "simulated" | "none";
  /** Count + last sample time. Geometry stays off the 1s body unless asked. */
  trailRevision: string;
  flightTrack?: unknown;
};

export function fnv1aHex(input: string): string {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function payloadOf(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== "object") return {};
  const rec = raw as Record<string, unknown>;
  if (rec.payload && typeof rec.payload === "object") return rec.payload as Record<string, unknown>;
  return rec;
}

function companyBits(payload: Record<string, unknown>) {
  const company = payload.company && typeof payload.company === "object" ? (payload.company as Record<string, unknown>) : {};
  const crew = Array.isArray(payload.crew) ? payload.crew : [];
  return {
    id: typeof company.id === "string" ? company.id : "",
    operatorName: typeof company.operatorName === "string" ? company.operatorName : "",
    inviteCode: typeof company.inviteCode === "string" ? company.inviteCode : "",
    theme: payload.theme ?? company.theme ?? null,
    themePack: payload.themePack ?? company.themePack ?? null,
    themeAccent: payload.themeAccent ?? company.themeAccent ?? null,
    deskChrome: payload.deskChrome ?? null,
    crew: crew.map((row) => {
      const rec = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
      return { id: rec.id ?? "", displayName: rec.displayName ?? "" };
    }),
    removedCrewIds: Array.isArray(company.removedCrewIds) ? company.removedCrewIds : [],
    removedInboxIds: Array.isArray(company.removedInboxIds) ? company.removedInboxIds : [],
    removedMissionIds: Array.isArray(company.removedMissionIds) ? company.removedMissionIds : [],
    removedFlightLogIds: Array.isArray(company.removedFlightLogIds) ? company.removedFlightLogIds : [],
    activeFlightId: typeof company.activeFlightId === "string" ? company.activeFlightId : "",
    activeFlightAuthority: company.activeFlightAuthority ?? "",
    activeFlightAt: typeof company.activeFlightAt === "string" ? company.activeFlightAt : "",
    ...widgetLayoutCompanyBits(company),
  };
}

/**
 * Null when the widget editor is off and no board was edited.
 * Spread into company bits so an untouched company keeps the previous hash.
 * apps/sync-server scheduleCompanyBits must spread the same keys.
 */
function widgetLayoutCompanyBits(company: Record<string, unknown>): Record<string, unknown> {
  const bits = widgetLayoutScheduleBits(company);
  if (!bits) return {};
  return bits;
}

function dutyBits(payload: Record<string, unknown>) {
  const duty = payload.duty && typeof payload.duty === "object" ? (payload.duty as Record<string, unknown>) : {};
  const sessions = Array.isArray(duty.sessions) ? duty.sessions : Array.isArray(payload.duty) ? payload.duty : [];
  return sessions.map((row) => {
    const rec = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
    return {
      crewId: rec.crewId ?? "",
      status: rec.status ?? "",
      startedAtUtc: rec.startedAtUtc ?? "",
      restStartedAtUtc: rec.restStartedAtUtc ?? "",
    };
  });
}

/**
 * Stored ops-NOTAM brief → schedule hash text.
 * Keep identical to apps/sync-server scheduleNotamBits.
 * fetchedAt is not part of the hash (a refresh must not push).
 */
export function scheduleNotamBits(raw: unknown): string {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return "";
  const rec = raw as Record<string, unknown>;
  const status = typeof rec.status === "string" ? rec.status : "";
  if (status !== "ok" && status !== "unavailable" && status !== "last-known" && status !== "needs-source") return "";
  const items = Array.isArray(rec.items) ? rec.items : [];
  const lines: string[] = [];
  for (const n of items) {
    if (!n || typeof n !== "object" || Array.isArray(n)) continue;
    const row = n as Record<string, unknown>;
    const impact = typeof row.impact === "string" ? row.impact : "";
    if (impact !== "rwy" && impact !== "twy" && impact !== "ad" && impact !== "lighting" && impact !== "navaid") continue;
    lines.push(`${row.id ?? ""}|${row.icao ?? ""}|${impact}|${row.text ?? ""}`);
  }
  const rawCount = typeof rec.rawCount === "number" && Number.isFinite(rec.rawCount) ? rec.rawCount : items.length;
  return JSON.stringify({ status, rawCount, items: lines });
}

/** Missions / assignments / company chrome — not live lat/lon. */
export function scheduleFingerprint(raw: unknown): string {
  const payload = payloadOf(raw);
  return fnv1aHex(
    JSON.stringify({
      missions: payload.missions ?? [],
      assignments: payload.assignments ?? [],
      assignmentAudit: payload.assignmentAudit ?? [],
      inbox: payload.inbox ?? [],
      company: companyBits(payload),
      opsNotamBrief: scheduleNotamBits(payload.opsNotamBrief),
      duty: dutyBits(payload),
    }),
  );
}

export const SCHEDULE_HASH_MISMATCH_NOTICE =
  "SYNC  schedule hash does not match this Nanode revision. This desk did not ack it. Pull again to recover.";

/**
 * Ack a company schedule revision only after GET /v1/state applied and the
 * snapshot's schedule hash equals the cheap /v1/revision ping.
 * A failed pull leaves the previous ack alone. A mismatch does not take the ping.
 */
export function decideScheduleRevisionAck(input: {
  pullOk: boolean;
  advertisedRevision?: string | null;
  receivedRevision?: string | null;
}): { ack: string | null; mismatch: boolean; notice: string } {
  if (!input.pullOk) {
    return { ack: null, mismatch: false, notice: "" };
  }
  const advertised = String(input.advertisedRevision ?? "").trim();
  const received = String(input.receivedRevision ?? "").trim();
  if (!advertised || !received || advertised !== received) {
    return { ack: "", mismatch: true, notice: SCHEDULE_HASH_MISMATCH_NOTICE };
  }
  return { ack: received, mismatch: false, notice: "" };
}

/**
 * Trail identity only (trip, count, last time) — not the coordinate list.
 * Keep the JSON shape identical to apps/sync-server revisionMeta.
 * scheduleFingerprint (company bits, NOTAM bits, duty) must match that server hash.
 */
export function trailFingerprintBits(payload: Record<string, unknown>): Array<{ tripId: string; n: number; at: string }> {
  const tracks = Array.isArray(payload.flightTracks) ? payload.flightTracks : [];
  return tracks.map((row) => {
    const rec = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
    const points = Array.isArray(rec.points) ? rec.points : [];
    const last = points.length && points[points.length - 1] && typeof points[points.length - 1] === "object"
      ? (points[points.length - 1] as Record<string, unknown>)
      : {};
    return {
      tripId: typeof rec.tripId === "string" ? rec.tripId : "",
      n: points.length,
      at: typeof last.atUtc === "string" ? last.atUtc : "",
    };
  });
}

/** Desk feed on `/v1/live`. Missing or unknown stays `none` — staleness is not a source. */
export function liveSimSource(raw: unknown): "simconnect" | "simulated" | "none" {
  const value = payloadOf(raw).simSource;
  if (value === "simconnect" || value === "simulated" || value === "none") return value;
  return "none";
}

/** Position / presence, plus trail identity so a new sample is visible without a board push. */
export function liveFingerprint(raw: unknown): string {
  const payload = payloadOf(raw);
  return fnv1aHex(
    JSON.stringify({
      crewPresence: payload.crewPresence ?? [],
      lastKnownPosition: payload.lastKnownPosition ?? null,
      simSource: liveSimSource(raw),
      trail: trailFingerprintBits(payload),
    }),
  );
}

export function revisionMetaFromSnapshot(raw: unknown): SyncRevisionMeta {
  const payload = payloadOf(raw);
  const company = payload.company && typeof payload.company === "object" ? (payload.company as Record<string, unknown>) : {};
  const exportedAtUtc =
    raw && typeof raw === "object" && typeof (raw as { exportedAtUtc?: unknown }).exportedAtUtc === "string"
      ? (raw as { exportedAtUtc: string }).exportedAtUtc
      : "";
  const chrome = deskChromeFromUnknown(payload);
  return {
    scheduleRevision: scheduleFingerprint(raw),
    liveRevision: liveFingerprint(raw),
    exportedAtUtc,
    companyId: typeof company.id === "string" ? company.id : "",
    companyName: typeof company.operatorName === "string" ? company.operatorName : "",
    theme: chrome.theme,
    themePack: chrome.themePack,
    themeAccent: chrome.themeAccent,
    deskVersion:
      raw && typeof raw === "object" && typeof (raw as { deskVersion?: unknown }).deskVersion === "string"
        ? (raw as { deskVersion: string }).deskVersion.trim()
        : "",
  };
}

/** True when Nanode included desk chrome on the cheap ping. Empty = old server; do not apply defaults. */
export function revisionCarriesDeskChrome(rev: Pick<SyncRevisionMeta, "themePack" | "themeAccent">): boolean {
  return Boolean(rev.themePack?.trim() || rev.themeAccent?.trim());
}

export function liveSliceFromSnapshot(raw: unknown): SyncLiveSlice {
  const payload = payloadOf(raw);
  const meta = revisionMetaFromSnapshot(raw);
  const track = latestFlightTrack(hydrateFlightTracks(payload.flightTracks));
  return {
    liveRevision: meta.liveRevision,
    exportedAtUtc: meta.exportedAtUtc,
    crewPresence: payload.crewPresence ?? [],
    lastKnownPosition: payload.lastKnownPosition ?? null,
    simSource: liveSimSource(raw),
    trailRevision: flightTrackFingerprint(track),
    flightTrack: track,
  };
}
